import React, { useRef, useEffect, useMemo, useState } from 'react';
import Select from "react-select";
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import ApiAluno from '../services/alunoService';
import ApiAlunoTemp from '../services/alunoTempService';
import ApiEventos from '../services/eventoService';
import ApiFaixas from '../services/faixaService';
import ApiMunicipios from '../services/municipioService';
import ApiDivisoes from '../services/divisaoService';

import ValorMonetario from '../components/ValorMonetario';
import RangeSelect from '../components/RangeSelect';
import FormaPagamento from '../components/FormaPagamento';
import GyeorugiTk3NaoFiliado from './gyeorugi-tk3-nao-filiado';

const getAtletaInicial = () => ({
    id: '',
    nome: '',
    sexo: '',
    cpf: '',
    email: '',
    data_nascimento: '',
    peso: '',
    altura: '',
    tecnico: '',
    academia: '',
    categoria_faixa: '',
    municipio_id: '',
    maiorIdade: '',
    idade: null,
    categorias: [],
    categoria: '',
    codigo: '',
    divisao: '',
    rangePeso: null,
    rangeAltura: null,
});

const EventoInscricaoNaoFiliado = ({ evento }) => {
    const navigate = useNavigate();
    const boxPedidoRef = useRef(null);
    const processingRef = useRef(false);

    const [tipoGyeorugi, setTipoGyeorugi] = useState('individual');
    const [modalidade, setModalidade] = useState('');
    const [pedido, setPedido] = useState(null);
    const [listaFaixas, setListaFaixas] = useState([]);
    const [listaMunicipios, setListaMunicipios] = useState([]);
    const [isCheckedGyeorugi, setIsCheckedGyeorugi] = useState(false);
    const [isCheckedPoomsae, setIsCheckedPoomsae] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [atletas, setAtletas] = useState([getAtletaInicial()]);

    const texto = evento.tipoEvento !== 3 ? 'Gerar Boleto' : 'Realizar Inscrição';
    const isDuplas = modalidade === 'Duplas';
    const atletasRenderizados = isDuplas ? atletas : [atletas[0]];
    const podeExibirGyeorugi = evento?.gyeorugi === 'sim' && !isDuplas;
    const podeExibirPoomsae = evento?.poomsae === 'sim';

    const inscricaoAberta = useMemo(() => {
        if (!evento?.inicioInscricao || !evento?.fimInscricao) return false;

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const inicio = new Date(evento.inicioInscricao);
        inicio.setHours(0, 0, 0, 0);

        const fim = new Date(evento.fimInscricao);
        fim.setHours(23, 59, 59, 999);

        return hoje >= inicio && hoje <= fim;
    }, [evento]);

    useEffect(() => {
        obterListaFaixas();
        obterListaMunicipios();
    }, []);

    useEffect(() => {
        if (isDuplas) {
            setIsCheckedPoomsae(true);
            setIsCheckedGyeorugi(false);

            setAtletas((prev) => {
                if (prev.length === 2) return prev;

                return [
                    prev[0] ?? getAtletaInicial(),
                    getAtletaInicial()
                ];
            });
        } else {
            setAtletas((prev) => [prev[0] ?? getAtletaInicial()]);
        }
    }, [isDuplas]);

    const handleTipoGyeorugiChange = (tipo) => {
        setTipoGyeorugi(tipo);

        if (tipo === 'tk3') {
            setIsCheckedGyeorugi(false);
            setIsCheckedPoomsae(false);
            setModalidade('');
        }
    };

    const handleToggleGyeorugi = () => {
        if (isDuplas) {
            setIsCheckedGyeorugi(false);
            return;
        }

        setIsCheckedGyeorugi((prev) => !prev);
    };

    const handleTogglePoomsae = () => {
        if (isDuplas) {
            setIsCheckedPoomsae(true);
            return;
        }

        setIsCheckedPoomsae((prev) => !prev);
    };

    const handleChangeAtleta = (index, campo, valor) => {
        setAtletas((prev) =>
            prev.map((atleta, i) =>
                i === index ? { ...atleta, [campo]: valor } : atleta
            )
        );
    };

    const getNomeFaixa = (faixaId) => {
        const faixa = listaFaixas.find((item) => String(item.id) === String(faixaId));
        return faixa?.faixa || '';
    };

    const obterListaFaixas = async () => {
        const response = await ApiFaixas.listar();

        if (response.statusRetorno !== 200) {
            Swal.fire({
                title: 'Erro ao carregar faixas!',
                text: response.message,
                icon: 'error',
                confirmButtonText: 'Ok'
            });
            return;
        }

        setListaFaixas(response.data);
    };

    const obterListaMunicipios = async () => {
        const response = await ApiMunicipios.listar();

        if (response.statusRetorno !== 200) {
            Swal.fire({
                title: 'Erro ao carregar Municípios!',
                text: response.message,
                icon: 'error',
                confirmButtonText: 'Ok'
            });
            return;
        }

        setListaMunicipios(response.data);
    };

    const obterDivisoes = async (index, value, idade) => {
        handleChangeAtleta(index, 'maiorIdade', value);
        handleChangeAtleta(index, 'categoria', '');
        handleChangeAtleta(index, 'codigo', '');
        handleChangeAtleta(index, 'divisao', '');
        handleChangeAtleta(index, 'rangePeso', null);
        handleChangeAtleta(index, 'rangeAltura', null);

        if (value === '') return;

        try {
            const response = await ApiDivisoes.listarPorTipoIdade(value);

            if (response.statusRetorno !== 200) {
                Swal.fire({
                    title: 'Erro ao obter divisões!',
                    text: response.message,
                    icon: 'error',
                    confirmButtonText: 'Ok'
                });
                return;
            }

            const divisoesCompativeis = response.data.filter((item) => {
                const idadeInicio = parseInt(item.idade_inicio, 10);
                const idadeFim = parseInt(item.idade_fim, 10);

                return idade >= idadeInicio && idade <= idadeFim;
            });

            setAtletas((prev) =>
                prev.map((atleta, i) =>
                    i === index
                        ? { ...atleta, categorias: divisoesCompativeis }
                        : atleta
                )
            );
        } catch {
            Swal.fire({
                title: 'Erro ao obter divisões!',
                icon: 'error',
                confirmButtonText: 'Ok'
            });
        }
    };

    const atualizarDadosNascimento = async (index, dataNascimento) => {
        if (!dataNascimento) return;

        const nascimento = new Date(dataNascimento);
        if (isNaN(nascimento.getTime())) return;

        const anoNascimento = nascimento.getFullYear();
        const hoje = new Date();
        const anoAtual = hoje.getFullYear();

        if (anoNascimento < 1900 || anoNascimento > anoAtual) return;

        let idadeCalculada = anoAtual - anoNascimento;
        const mes = hoje.getMonth() - nascimento.getMonth();

        if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
            idadeCalculada--;
        }

        const idadeNoFimDoAno = anoAtual - anoNascimento;
        const idadeEhMaior = idadeNoFimDoAno >= 18 ? '1' : '0';

        handleChangeAtleta(index, 'idade', idadeCalculada);

        await obterDivisoes(
            index,
            idadeEhMaior,
            idadeNoFimDoAno
        );
    };

    const atualizarFaixaPesoAltura = (index, atletaAtualizado) => {
        if (!atletaAtualizado.sexo || !atletaAtualizado.categoria) {
            handleChangeAtleta(index, 'rangePeso', null);
            handleChangeAtleta(index, 'rangeAltura', null);
            handleChangeAtleta(index, 'codigo', '');
            handleChangeAtleta(index, 'divisao', '');
            return;
        }

        const categoriaSelecionada = (atletaAtualizado.categorias || []).find(
            (cat) => Number(cat.id) === Number(atletaAtualizado.categoria)
        );

        if (!categoriaSelecionada) {
            handleChangeAtleta(index, 'rangePeso', null);
            handleChangeAtleta(index, 'rangeAltura', null);
            handleChangeAtleta(index, 'codigo', '');
            handleChangeAtleta(index, 'divisao', '');
            return;
        }

        let rangePesoSexo;

        if (evento?.tipoEvento === 3) {
            rangePesoSexo = atletaAtualizado.sexo === 'Masculino'
                ? categoriaSelecionada.range_peso_masculino_jir
                : categoriaSelecionada.range_peso_feminino_jir;
        } else {
            rangePesoSexo = atletaAtualizado.sexo === 'Masculino'
                ? categoriaSelecionada.range_peso_masculino
                : categoriaSelecionada.range_peso_feminino;
        }

        const rangeAlturaSexo = atletaAtualizado.sexo === 'Masculino'
            ? categoriaSelecionada.range_altura_masculino
            : categoriaSelecionada.range_altura_feminino;

        handleChangeAtleta(index, 'rangePeso', rangePesoSexo);
        handleChangeAtleta(index, 'rangeAltura', rangeAlturaSexo);
        handleChangeAtleta(index, 'divisao', categoriaSelecionada.divisao);
        handleChangeAtleta(index, 'codigo', categoriaSelecionada.codigo);
    };

    useEffect(() => {
        const atleta = atletas[0];

        if (!atleta) return;

        const idadeNoFimDoAno = atleta.data_nascimento
            ? new Date().getFullYear() - new Date(atleta.data_nascimento).getFullYear()
            : null;

        if (
            !isDuplas &&
            atleta.data_nascimento &&
            evento.tipoEvento === 3 &&
            idadeNoFimDoAno !== null &&
            idadeNoFimDoAno < 16 &&
            isCheckedGyeorugi
        ) {
            setIsCheckedGyeorugi(false);

            Swal.fire({
                title: 'Ops!',
                text: 'A idade mínima para participar do Gyeorugi neste evento é 16 anos.',
                icon: 'error',
                confirmButtonText: 'Ok'
            });
        }
    }, [atletas, evento, isCheckedGyeorugi, isDuplas]);

    const buscarCpf = async (index, cpfDigitado) => {
        const cpfLimpo = cpfDigitado.replace(/\D/g, '');

        if (cpfLimpo.length !== 11) return;

        try {
            const retorno = await ApiAluno.getAlunoPorCpf(cpfLimpo);

            if (retorno.data.length > 0) {
                Swal.fire({
                    title: 'Atenção',
                    text: 'Este CPF já está vinculado à FETRON!',
                    icon: 'warning',
                    confirmButtonText: 'Efetuar Login'
                }).then(() => {
                    navigate('/login');
                });

                return;
            }

            const response = await ApiAlunoTemp.obterPorCpf(cpfLimpo);

            if (!response) return;

            const aluno = response.data;

            const novoAtleta = {
                ...atletas[index],
                nome: aluno.nome || '',
                sexo: aluno.sexo || '',
                cpf: aluno.cpf || cpfDigitado,
                data_nascimento: aluno.dataNascimento || '',
                email: aluno.email || '',
                categoria_faixa: aluno.faixaId?.toString() || '',
                tecnico: aluno.tecnico || '',
                academia: aluno.academia || '',
                id: aluno.id || '',
                municipio_id: aluno.municipioId || '',
            };

            setAtletas((prev) =>
                prev.map((item, i) =>
                    i === index ? novoAtleta : item
                )
            );

            if (novoAtleta.data_nascimento) {
                await atualizarDadosNascimento(
                    index,
                    novoAtleta.data_nascimento
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const validarCpf = (cpf) => {
        const cpfLimpo = cpf.replace(/\D/g, '');

        if (cpfLimpo.length !== 11) return false;
        if (/^(\d)\1{10}$/.test(cpfLimpo)) return false;

        let soma = 0;
        let resto;

        for (let i = 1; i <= 9; i++) {
            soma += parseInt(cpfLimpo.substring(i - 1, i), 10) * (11 - i);
        }

        resto = (soma * 10) % 11;

        if (resto === 10 || resto === 11) resto = 0;
        if (resto !== parseInt(cpfLimpo.substring(9, 10), 10)) return false;

        soma = 0;

        for (let i = 1; i <= 10; i++) {
            soma += parseInt(cpfLimpo.substring(i - 1, i), 10) * (12 - i);
        }

        resto = (soma * 10) % 11;

        if (resto === 10 || resto === 11) resto = 0;
        if (resto !== parseInt(cpfLimpo.substring(10, 11), 10)) return false;

        return true;
    };

    const handleChangeCpf = async (index, valor) => {
        handleChangeAtleta(index, 'cpf', valor);

        const cpfLimpo = valor.replace(/\D/g, '');

        if (cpfLimpo.length !== 11) return;

        if (!validarCpf(valor)) {
            Swal.fire({
                title: 'CPF inválido!',
                text: 'Número de CPF digitado é inválido.',
                icon: 'error',
                confirmButtonText: 'Ok'
            });
            return;
        }

        await buscarCpf(index, valor);
    };

    const handleBlurCpf = async (index) => {
        const atleta = atletas[index];
        const cpfLimpo = (atleta?.cpf || '').replace(/\D/g, '');

        if (cpfLimpo.length === 11 && validarCpf(atleta.cpf)) {
            await buscarCpf(index, atleta.cpf);
        }
    };

    const validarAtleta = (atleta, index, considerarGyeorugi = false) => {
        const erros = [];
        const prefixo = isDuplas ? `• Atleta ${index + 1}: ` : '• ';

        if (!atleta.cpf) erros.push(`${prefixo}informe o CPF.`);
        if (atleta.cpf && !validarCpf(atleta.cpf)) erros.push(`${prefixo}CPF inválido.`);
        if (!atleta.nome) erros.push(`${prefixo}informe o nome.`);
        if (!atleta.sexo) erros.push(`${prefixo}informe o sexo.`);
        if (!atleta.email) erros.push(`${prefixo}informe o email.`);

        if (!atleta.categoria_faixa || atleta.categoria_faixa === '0') {
            erros.push(`${prefixo}a graduação é obrigatória.`);
        }

        if (!atleta.data_nascimento) erros.push(`${prefixo}informe a data de nascimento.`);
        if (!atleta.municipio_id) erros.push(`${prefixo}informe o município.`);
        if (!atleta.tecnico) erros.push(`${prefixo}informe o técnico.`);
        if (!atleta.academia) erros.push(`${prefixo}informe a academia.`);

        if (considerarGyeorugi) {
            if (!atleta.categoria) {
                erros.push(`${prefixo}selecione a categoria do Gyeorugi.`);
            }

            if (!atleta.peso && !atleta.altura) {
                erros.push(`${prefixo}informe o peso ou altura.`);
            }
        }

        return erros;
    };

    const salvarAtletaTemp = async (atleta) => {
        return ApiAlunoTemp.save(atleta);
    };

    const finalizarPedido = (pedidoInscricao) => {
        setPedido(pedidoInscricao);

        setTimeout(() => {
            boxPedidoRef.current?.scrollIntoView({
                behavior: 'smooth'
            });
        }, 100);
    };

    const handlePagamentoIndividual = async (alunoTempId) => {
        const atleta = atletas[0];

        const divisaoCodigo = atleta.divisao && atleta.codigo
            ? `${atleta.divisao}/${atleta.codigo}`
            : '';

        const data = {
            usuarioId: 0,
            alunoId: 0,
            alunoTempId,
            eventoId: evento.id,
            cpf: atleta.cpf,
            nome: atleta.nome,
            email: atleta.email,
            peso: atleta.peso,
            altura: atleta.altura,
            municipioId: atleta.municipio_id,
            divisaoCodigo,
            gyeorugi: isCheckedGyeorugi,
            modalidade,
            poomsae: isCheckedPoomsae,
            gerarPedido: evento.tipoEvento === 3 ? false : true,
        };

        const response = await ApiEventos.pagamentoSemVinculo(data);

        if (response.statusRetorno !== 200) {
            Swal.fire({
                title: 'Houve um erro ao gerar sua cobrança!',
                text: response.message,
                icon: 'error',
                confirmButtonText: 'Ok'
            });
            return;
        }

        if (response.status === 'error') {
            Swal.fire({
                title: 'Hummm!',
                text: response.message,
                icon: 'warning',
                confirmButtonText: 'Ok'
            });
            return;
        }

        if (response.pedido?.status === 'error') {
            Swal.fire({
                title: 'Hummm!',
                text: response.pedido.message,
                icon: 'warning',
                confirmButtonText: 'Ok'
            });
            return;
        }

        finalizarPedido(response.pedido);
    };

    const handlePagamentoDuplas = async (alunosSalvos) => {
        const payload = {
            alunos: alunosSalvos.map((aluno, index) => ({
                alunoId: 0,
                alunoTempId: aluno.id,
                modalidade: 'Duplas',
                maiorIdade: atletas[index].maiorIdade,
                categoria: getNomeFaixa(atletas[index].categoria_faixa),
            })),
            gerarPedido: evento.tipoEvento === 3 ? false : true,
            eventoId: evento.id,
            token: localStorage.getItem('token') || '',
            usuarioId: Number(localStorage.getItem('usuarioId') || 0),
            tipoEvento: 'Poomsae',
            nome: atletas[0].nome,
            cpf: atletas[0].cpf,
            email: atletas[0].email
        };

        const response = await ApiEventos.pagamentoSemVinculoDuplas(payload);

        if (response.statusRetorno !== 200) {
            Swal.fire({
                title: 'Houve um erro ao gerar sua cobrança!',
                text: response.message,
                icon: 'error',
                confirmButtonText: 'Ok'
            });
            return;
        }

        if (response.status === 'error') {
            Swal.fire({
                title: 'Hummm!',
                text: response.message,
                icon: 'warning',
                confirmButtonText: 'Ok'
            });
            return;
        }

        if (response.pedido?.status === 'error') {
            Swal.fire({
                title: 'Hummm!',
                text: response.pedido.message,
                icon: 'warning',
                confirmButtonText: 'Ok'
            });
            return;
        }

        finalizarPedido(response.pedido);
    };

    const handleInscricao = async () => {
        if (processingRef.current) return;

        const erros = [];

        if (isDuplas) {
            if (!isCheckedPoomsae) {
                erros.push('• Inscrição em duplas permite apenas Poomsae.');
            }

            if (isCheckedGyeorugi) {
                erros.push('• Em duplas, o Gyeorugi deve ser feito em uma nova inscrição separada.');
            }

            if (!modalidade) {
                erros.push('• Selecione a modalidade do Poomsae.');
            }

            atletas.forEach((atleta, index) => {
                erros.push(...validarAtleta(atleta, index, false));
            });

            const cpfs = atletas
                .map((atleta) => (atleta.cpf || '').replace(/\D/g, ''))
                .filter(Boolean);

            if (cpfs.length === 2 && cpfs[0] === cpfs[1]) {
                erros.push('• Os atletas da dupla devem ser diferentes.');
            }
        } else {
            const atleta = atletas[0];

            erros.push(...validarAtleta(
                atleta,
                0,
                isCheckedGyeorugi
            ));

            if (isCheckedPoomsae && !modalidade) {
                erros.push('• Selecione a modalidade do Poomsae.');
            }

            if (!isCheckedGyeorugi && !isCheckedPoomsae) {
                erros.push('• Selecione ao menos uma modalidade para participar.');
            }
        }

        if (erros.length > 0) {
            Swal.fire({
                title: 'Atenção!',
                html: erros.join('<br>'),
                icon: 'warning',
                confirmButtonText: 'Ok'
            });
            return;
        }

        processingRef.current = true;
        setIsProcessing(true);

        try {
            const atletasParaSalvar = isDuplas ? atletas : [atletas[0]];
            const alunosSalvos = [];

            for (const atleta of atletasParaSalvar) {
                const response = await salvarAtletaTemp(atleta);

                if (response.status !== 'success') {
                    Swal.fire(
                        'Atenção',
                        `Erro ao realizar inscrição: ${response.message}`,
                        'error'
                    );
                    return;
                }

                alunosSalvos.push(response.aluno);
            }

            Swal.fire(
                'Dados cadastrados com sucesso!',
                'Aguarde enquanto estamos processando sua inscrição!',
                'success'
            );

            if (isDuplas) {
                await handlePagamentoDuplas(alunosSalvos);
                return;
            }

            await handlePagamentoIndividual(alunosSalvos[0].id);
        } catch (error) {
            console.error(error);

            Swal.fire({
                title: 'Erro ao processar a inscrição!',
                icon: 'error',
                confirmButtonText: 'Ok'
            });
        } finally {
            processingRef.current = false;
            setIsProcessing(false);
        }
    };

    const renderFormularioAtleta = (atleta, index) => {
        const optionsMunicipios = listaMunicipios.map((municipio) => ({
            value: municipio.id,
            label: municipio.municipio,
        }));

        return (
            <div key={index} className="col-md-12 mb-5">
                <div className="row">
                    <div className="col-12">
                        <h5>
                            {isDuplas
                                ? `Cadastro do Atleta Convidado ${index + 1}`
                                : 'Cadastro de Atleta Convidado'
                            }
                        </h5>
                    </div>

                    <div className="col-md-2">
                        <label>CPF *</label>
                        <input
                            maxLength="11"
                            type="text"
                            value={atleta.cpf}
                            className="form-control"
                            onChange={(e) => handleChangeCpf(index, e.target.value)}
                            onBlur={() => handleBlurCpf(index)}
                        />
                    </div>

                    <div className="col-md-5">
                        <label>Nome *</label>
                        <input
                            type="text"
                            maxLength="100"
                            value={atleta.nome}
                            className="form-control"
                            onChange={(e) => handleChangeAtleta(index, 'nome', e.target.value)}
                        />
                    </div>

                    <div className="col-md-5">
                        <label>Email *</label>
                        <input
                            type="text"
                            maxLength="100"
                            value={atleta.email}
                            className="form-control"
                            onChange={(e) => handleChangeAtleta(index, 'email', e.target.value)}
                        />
                    </div>

                    <div className="col-md-4 mt-3">
                        <label>Categoria/Faixa *</label>

                        <select
                            value={atleta.categoria_faixa}
                            className="form-control"
                            onChange={(e) =>
                                handleChangeAtleta(
                                    index,
                                    'categoria_faixa',
                                    e.target.value
                                )
                            }
                        >
                            <option value="0">::Selecione::</option>

                            {listaFaixas
                                .filter((faixa) => {
                                    if (evento?.tipoEvento === 3) {
                                        return faixa.id >= 9;
                                    }

                                    return true;
                                })
                                .map((faixa) => (
                                    <option key={faixa.id} value={faixa.id}>
                                        {faixa.faixa}
                                    </option>
                                ))
                            }
                        </select>
                    </div>

                    <div className="col-md-2 mt-3">
                        <label>Sexo *</label>

                        <select
                            value={atleta.sexo}
                            className="form-control"
                            onChange={(e) => {
                                const atualizado = {
                                    ...atleta,
                                    sexo: e.target.value
                                };

                                handleChangeAtleta(
                                    index,
                                    'sexo',
                                    e.target.value
                                );

                                atualizarFaixaPesoAltura(
                                    index,
                                    atualizado
                                );
                            }}
                        >
                            <option value="">::Selecione::</option>
                            <option value="Masculino">Masculino</option>
                            <option value="Feminino">Feminino</option>
                        </select>
                    </div>

                    <div className="col-md-3 mt-3">
                        <label>Data de Nascimento *</label>

                        <input
                            type="date"
                            value={atleta.data_nascimento}
                            className="form-control"
                            onChange={async (e) => {
                                handleChangeAtleta(
                                    index,
                                    'data_nascimento',
                                    e.target.value
                                );

                                await atualizarDadosNascimento(
                                    index,
                                    e.target.value
                                );
                            }}
                        />
                    </div>

                    <div className="col-md-3 mt-3">
                        <label>Idade *</label>

                        <select
                            value={atleta.maiorIdade}
                            disabled
                            readOnly
                            className="form-control"
                        >
                            <option value="">::Selecione::</option>
                            <option value="1">18 +</option>
                            <option value="0">18 -</option>
                        </select>
                    </div>

                    <div className="col-md-4 mt-3">
                        <label>Técnico *</label>

                        <input
                            type="text"
                            maxLength="100"
                            value={atleta.tecnico}
                            className="form-control"
                            onChange={(e) =>
                                handleChangeAtleta(
                                    index,
                                    'tecnico',
                                    e.target.value
                                )
                            }
                        />
                    </div>

                    <div className="col-md-4 mt-3">
                        <label>Academia *</label>

                        <input
                            maxLength="100"
                            type="text"
                            value={atleta.academia}
                            className="form-control"
                            onChange={(e) =>
                                handleChangeAtleta(
                                    index,
                                    'academia',
                                    e.target.value
                                )
                            }
                        />
                    </div>

                    <div className="col-md-4 mt-3">
                        <label>Município *</label>

                        <Select
                            options={optionsMunicipios}
                            value={
                                optionsMunicipios.find(
                                    (option) => option.value === atleta.municipio_id
                                ) || null
                            }
                            onChange={(selected) =>
                                handleChangeAtleta(
                                    index,
                                    'municipio_id',
                                    selected?.value || ''
                                )
                            }
                            placeholder="Digite para buscar..."
                            isSearchable
                            noOptionsMessage={() => 'Nenhum município encontrado'}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const atletaPrincipal = atletas[0] || getAtletaInicial();

    return (
        <>
            {inscricaoAberta ? (
                <div className="container z-9">

                    {evento?.gyeorugi === 'sim' && !pedido && (
                        <div className="row mb-5">
                            <div className="col-md-12">
                                <div className="de_pricing-table type-2">
                                    <div className="d-head">
                                        <h3>Gyeorugi</h3>
                                    </div>

                                    <div className="text-center mt-3">
                                        <h4>Como deseja participar?</h4>
                                    </div>

                                    <div className="row justify-content-center mt-4">
                                        <div className="col-md-4 mb-3">
                                            <button
                                                type="button"
                                                className={`btn-main w-100 ${
                                                    tipoGyeorugi === 'individual'
                                                        ? 'color-2'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleTipoGyeorugiChange('individual')
                                                }
                                            >
                                                Individual
                                            </button>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <button
                                                type="button"
                                                className={`btn-main w-100 ${
                                                    tipoGyeorugi === 'tk3'
                                                        ? 'color-2'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleTipoGyeorugiChange('tk3')
                                                }
                                            >
                                                TK3
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {tipoGyeorugi === 'tk3' && evento?.gyeorugi === 'sim' ? (
                        <GyeorugiTk3NaoFiliado evento={evento} />
                    ) : (
                        <>
                            <div className="row">
                                {atletasRenderizados.map((atleta, index) =>
                                    renderFormularioAtleta(atleta, index)
                                )}
                            </div>

                            {(podeExibirGyeorugi || podeExibirPoomsae) && (
                                <div className="row mt-4">

                                    {podeExibirGyeorugi && (
                                        <div className="col-xl-4 col-md-4 mb-sm-30 fadeInRight">
                                            <div className="de_pricing-table type-2 h-100">
                                                <div className="d-head">
                                                    <h3>Gyeorugi</h3>
                                                </div>

                                                <div className="d-price">
                                                    <h4 className="opt-1">
                                                        <ValorMonetario valor={evento.taxaGeral} />
                                                    </h4>
                                                </div>

                                                <div className="spacer-5"></div>

                                                <div className="row">
                                                    <div className="col-12">
                                                        <ul className="d-list">
                                                            <li>
                                                                Categoria
                                                                <br />

                                                                <select
                                                                    className="form-control"
                                                                    value={atletaPrincipal.categoria}
                                                                    onChange={(e) => {
                                                                        const categoriaSelecionada = Number(e.target.value);

                                                                        const atualizado = {
                                                                            ...atletaPrincipal,
                                                                            categoria: categoriaSelecionada
                                                                        };

                                                                        handleChangeAtleta(
                                                                            0,
                                                                            'categoria',
                                                                            categoriaSelecionada
                                                                        );

                                                                        atualizarFaixaPesoAltura(
                                                                            0,
                                                                            atualizado
                                                                        );
                                                                    }}
                                                                    disabled={!atletaPrincipal.maiorIdade}
                                                                >
                                                                    <option value="">
                                                                        ::Selecione::
                                                                    </option>

                                                                    {(atletaPrincipal.categorias || []).map((cat) => (
                                                                        <option key={cat.id} value={cat.id}>
                                                                            {cat.divisao}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </li>

                                                            {atletaPrincipal.rangePeso && (
                                                                <li>
                                                                    Peso (Kg)
                                                                    <br />

                                                                    <RangeSelect
                                                                        range={atletaPrincipal.rangePeso}
                                                                        className="form-control"
                                                                        value={atletaPrincipal.peso || ''}
                                                                        onChange={(value) =>
                                                                            handleChangeAtleta(
                                                                                0,
                                                                                'peso',
                                                                                value
                                                                            )
                                                                        }
                                                                        type="Kg"
                                                                        disabled={
                                                                            !atletaPrincipal.sexo ||
                                                                            !atletaPrincipal.categoria ||
                                                                            (
                                                                                atletaPrincipal.altura &&
                                                                                !atletaPrincipal.peso
                                                                            )
                                                                        }
                                                                    />
                                                                </li>
                                                            )}

                                                            {atletaPrincipal.rangeAltura && (
                                                                <li>
                                                                    Altura (Cm)
                                                                    <br />

                                                                    <RangeSelect
                                                                        range={atletaPrincipal.rangeAltura}
                                                                        className="form-control"
                                                                        value={atletaPrincipal.altura || ''}
                                                                        onChange={(value) =>
                                                                            handleChangeAtleta(
                                                                                0,
                                                                                'altura',
                                                                                value
                                                                            )
                                                                        }
                                                                        type="Cm"
                                                                        disabled={
                                                                            !atletaPrincipal.sexo ||
                                                                            !atletaPrincipal.categoria ||
                                                                            (
                                                                                atletaPrincipal.peso &&
                                                                                !atletaPrincipal.altura
                                                                            )
                                                                        }
                                                                    />
                                                                </li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>

                                                {atletaPrincipal.academia &&
                                                    atletaPrincipal.tecnico &&
                                                    atletaPrincipal.categoria &&
                                                    (
                                                        atletaPrincipal.peso ||
                                                        atletaPrincipal.altura
                                                    ) && (
                                                        <div className="d-action">
                                                            <input
                                                                className={`switch ${
                                                                    isCheckedGyeorugi
                                                                        ? 'checked'
                                                                        : 'unchecked'
                                                                }`}
                                                                type="checkbox"
                                                                checked={isCheckedGyeorugi}
                                                                onChange={handleToggleGyeorugi}
                                                            />{' '}
                                                            Quero Participar
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    )}

                                    {podeExibirPoomsae && (
                                        <div className="col-xl-4 col-md-4 mb-sm-30 fadeInRight">
                                            <div className="de_pricing-table type-2 h-100">
                                                <div className="d-head">
                                                    <h3>Poomsae</h3>
                                                </div>

                                                <div className="d-price">
                                                    <h4 className="opt-1">
                                                        <ValorMonetario valor={evento.taxaGeral} />
                                                    </h4>
                                                </div>

                                                <div className="d-location"></div>
                                                <div className="spacer-5"></div>

                                                <div className="d-group">
                                                    <ul className="d-list">
                                                        <li>
                                                            Modalidade
                                                            <br />

                                                            <select
                                                                className="form-control"
                                                                value={modalidade}
                                                                onChange={(e) =>
                                                                    setModalidade(e.target.value)
                                                                }
                                                            >
                                                                <option value="">
                                                                    ::Selecione::
                                                                </option>
                                                                <option value="Individual">
                                                                    Individual
                                                                </option>
                                                                <option value="FreeStyle">
                                                                    Free Style
                                                                </option>
                                                                <option value="Duplas">
                                                                    Duplas
                                                                </option>
                                                            </select>
                                                        </li>
                                                    </ul>
                                                </div>

                                                {isDuplas && (
                                                    <div className="alert alert-danger mt-3">
                                                        <strong>
                                                            Atenção, para inscrições de duplas, só é possível a inscrição para o Poomsae.
                                                            <br />
                                                            Caso deseje participar também do Gyeorugi, realize uma nova inscrição separada.
                                                        </strong>
                                                    </div>
                                                )}

                                                {modalidade && (
                                                    <div className="d-action">
                                                        <input
                                                            className={`switch ${
                                                                isCheckedPoomsae
                                                                    ? 'checked'
                                                                    : 'unchecked'
                                                            }`}
                                                            type="checkbox"
                                                            checked={isCheckedPoomsae}
                                                            onChange={handleTogglePoomsae}
                                                            disabled={isDuplas}
                                                        />{' '}
                                                        Quero Participar
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {(isCheckedGyeorugi || isCheckedPoomsae) && (
                                        <div className="col-xl-4 col-md-4 mb-sm-30 fadeInRight">
                                            <div className="de_pricing-table type-2">
                                                <div className="d-head">
                                                    <h3>{texto}</h3>
                                                </div>

                                                <div className="d-price">
                                                    <h4 className="opt-1">
                                                        Olá <span>{atletaPrincipal.nome}</span>, tudo bem?
                                                    </h4>

                                                    <h4 className="opt-1">
                                                        Você está se inscrevendo nas seguintes modalidades:
                                                    </h4>
                                                </div>

                                                <div className="spacer-5"></div>
                                                <div className="spacer-5"></div>

                                                <div className="d-group">
                                                    <ul className="d-list">
                                                        {isCheckedGyeorugi && (
                                                            <li>Gyeorugi</li>
                                                        )}

                                                        {isCheckedPoomsae && (
                                                            <li>
                                                                Poomsae{' '}
                                                                {isDuplas
                                                                    ? ' - Duplas'
                                                                    : modalidade
                                                                        ? ` - ${modalidade}`
                                                                        : ''
                                                                }
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>

                                                <div className="d-action">
                                                    {!pedido && (
                                                        <input
                                                            type="button"
                                                            value={
                                                                evento.tipoEvento !== 3
                                                                    ? (
                                                                        isProcessing
                                                                            ? 'Gerando Boleto...'
                                                                            : 'Efetuar Pagamento'
                                                                    )
                                                                    : (
                                                                        isProcessing
                                                                            ? 'Realizando Inscrição...'
                                                                            : 'Realizar Inscrição'
                                                                    )
                                                            }
                                                            className="btn-main color-2"
                                                            onClick={handleInscricao}
                                                            disabled={isProcessing}
                                                        />
                                                    )}

                                                    {evento.tipoEvento !== 3 && pedido !== null && (
                                                        <>
                                                            <h4 className="opt-1">
                                                                <span>
                                                                    Boleto gerado com sucesso!
                                                                </span>
                                                            </h4>

                                                            <p>
                                                                * Seu boleto e QRCODE para pagamento estão disponíveis abaixo!
                                                            </p>
                                                        </>
                                                    )}

                                                    {evento.tipoEvento === 3 && pedido !== null && (
                                                        <h4 className="opt-1">
                                                            <span>
                                                                Inscrição realizada com sucesso!
                                                            </span>
                                                        </h4>
                                                    )}

                                                    {evento.tipoEvento !== 3 && (
                                                        <p>
                                                            * Sua inscrição só será confirmada após a confirmação do pix ou da compensação do boleto!
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {pedido !== null &&
                                        pedido?.status !== 1 &&
                                        evento.tipoEvento !== 3 && (
                                            <div
                                                className="row mt-5"
                                                ref={boxPedidoRef}
                                            >
                                                <div className="col-xl-12 col-md-12 mb-sm-30 fadeInRight mb-5">
                                                    <div className="de_pricing-table type-2">
                                                        <FormaPagamento pedido={pedido} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : (
                evento && (
                    <div
                        style={{
                            textAlign: 'center',
                            padding: '2rem',
                            color: 'white'
                        }}
                    >
                        <h3>Inscrições indisponíveis</h3>

                        <h4 className="mt-5">
                            Período de inscrição:{' '}
                            <strong>
                                {new Date(evento.inicioInscricao).toLocaleDateString()}
                            </strong>{' '}
                            a{' '}
                            <strong>
                                {new Date(evento.fimInscricao).toLocaleDateString()}
                            </strong>
                        </h4>
                    </div>
                )
            )}
        </>
    );
};

export default EventoInscricaoNaoFiliado;